import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getHealth, getPlatformConfig } from '../../api/admin'
import styles from './Shell.module.css'

type Health = 'ok' | 'degraded' | 'down'

const HEALTH_LABEL: Record<Health, string> = { ok: 'В норме', degraded: 'С оговорками', down: 'Сбой' }
const HEALTH_TITLE: Record<Health, string> = {
  ok: 'Система в норме: все проверки проходят',
  degraded: 'Система работает с оговорками: часть проверок не проходит',
  down: 'Проверка готовности не проходит или сервер не отвечает',
}

/**
 * The platform's readiness at a glance: the same `/health/ready` the overview
 * reads (one shared query), as a dot. Opens the overview when this desk may.
 */
export function HealthIndicator({ canOpenOverview }: { canOpenOverview: boolean }): JSX.Element | null {
  const navigate = useNavigate()
  const health = useQuery({ queryKey: ['health'], queryFn: getHealth, refetchInterval: 15_000, retry: false })
  const state: Health | null = health.data ? (health.data.status === 'ok' ? 'ok' : 'degraded') : health.isError ? 'down' : null
  if (state === null) return null
  const content = (
    <>
      <span className={styles.healthDot} data-state={state} aria-hidden="true" />
      <span className={styles.healthLabel}>{HEALTH_LABEL[state]}</span>
    </>
  )
  return canOpenOverview ? (
    <button type="button" className={styles.health} data-state={state} title={HEALTH_TITLE[state]} aria-label={HEALTH_TITLE[state]} onClick={() => navigate('/')}>
      {content}
    </button>
  ) : (
    <span className={styles.health} data-state={state} title={HEALTH_TITLE[state]} role="img" aria-label={HEALTH_TITLE[state]}>
      {content}
    </span>
  )
}

const ZONE_SHORT: Record<string, string> = { 'Europe/Moscow': 'МСК', UTC: 'UTC', 'Etc/UTC': 'UTC' }

function zoneParts(timeZone: string | undefined): { zone: string | undefined; short: string; long: string } {
  const zone = timeZone && isValidZone(timeZone) ? timeZone : undefined
  const offset =
    new Intl.DateTimeFormat('ru-RU', { timeZone: zone, timeZoneName: 'shortOffset' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value ?? ''
  const long = new Intl.DateTimeFormat('ru-RU', { timeZone: zone, timeZoneName: 'long' }).formatToParts(new Date()).find((part) => part.type === 'timeZoneName')?.value ?? ''
  const short = (zone && ZONE_SHORT[zone]) ?? offset.replace(/^GMT/, 'UTC').replace(/^UTC$/, 'UTC±0')
  return { zone, short, long: zone ? `${long} · ${zone}` : long }
}

function isValidZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: zone })
    return true
  } catch {
    return false
  }
}

/** Wall-clock time in the platform's zone (`signals.display_timezone`), the zone signal times are written in. */
export function PlatformClock(): JSX.Element {
  const platform = useQuery({ queryKey: ['platform-config'], queryFn: getPlatformConfig, staleTime: 60_000 })
  const { zone, short, long } = useMemo(() => zoneParts(platform.data?.timezone), [platform.data?.timezone])
  const format = useMemo(() => new Intl.DateTimeFormat('ru-RU', { timeZone: zone, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }), [zone])
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    let timer = 0
    const tick = (): void => {
      setNow(new Date())
      timer = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 5)
    }
    timer = window.setTimeout(tick, 1000 - (Date.now() % 1000) + 5)
    return () => window.clearTimeout(timer)
  }, [])
  return (
    <span className={styles.clock} title={`Время платформы: ${long}`}>
      <time className={styles.clockTime} dateTime={now.toISOString()}>
        {format.format(now)}
      </time>
      <span className={styles.clockZone}>{short}</span>
    </span>
  )
}
